import { api } from './api';

// Utility function to retry failed requests
const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

class ListsService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL;
  }

  /**
   * Get all company lists from HubSpot, cross-referenced with database
   * Returns all lists with is_selected and is_active flags
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @returns {Promise<Object>} Lists data with summary
   */
  async getAllCompanyLists(authorizationToken, tenant) {
    try {
      console.log('Fetching all company lists');
      const endpoint = `/v1/lists/companies`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await retryRequest(async () => {
        return await api.get(endpoint, { headers });
      });

      console.log('Company lists response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching company lists:', error);
      throw error;
    }
  }

  /**
   * Get all contacts lists from HubSpot, cross-referenced with database
   * Returns all lists with is_selected and is_active flags
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @returns {Promise<Object>} Lists data with summary
   */
  async getAllContactsLists(authorizationToken, tenant) {
    try {
      console.log('Fetching all contacts lists');
      const endpoint = `/v1/lists/contacts`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await retryRequest(async () => {
        return await api.get(endpoint, { headers });
      });

      console.log('Contacts lists response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts lists:', error);
      throw error;
    }
  }

  /**
   * Get only active selected lists
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @returns {Promise<Object>} Active lists data
   */
  async getActiveLists(authorizationToken, tenant) {
    try {
      console.log('Fetching active lists');
      const endpoint = `/v1/lists/active`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await retryRequest(async () => {
        return await api.get(endpoint, { headers });
      });

      console.log('Active lists response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching active lists:', error);
      throw error;
    }
  }

  /**
   * Activate a list
   * Checks if activating would exceed the 30 company limit
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listId - List ID to activate
   * @param {string[]} signals - Optional list of signal values to save
   * @param {string} sellerValueProp - Optional seller value proposition
   * @param {string} sellerProofPoint - Optional seller proof point
   * @param {string} templateId - Optional template ID to associate with the list
   * @param {string} templateVersionId - Optional template version ID to use
   * @param {boolean} forceRefresh - Admin-only: skip cache and recalculate priorities
   * @returns {Promise<Object>} Activation response
   */
  async activateList(authorizationToken, tenant, listId, {
    signals = null,
    sellerValueProp = null,
    sellerProofPoint = null,
    templateId = null,
    templateVersionId = null,
    forceRefresh = false,
    timezone = null,
    sequenceSteps = null,
    // Plan 106: per-list personalized invite note (opt-in, default OFF)
    connectionNoteEnabled = null,
    connectionNoteTemplate = null,
    // Legacy fields (auto-converted to sequence_steps on backend if sequenceSteps not provided)
    outreachType = 'video',
    messageTemplateId = null,
    messageTemplateVersionId = null,
  } = {}) {
    try {
      console.log(`Activating list ${listId}`, signals ? `with ${signals.length} signals` : '', sequenceSteps ? `with ${sequenceSteps.length} steps` : '', forceRefresh ? '(force refresh)' : '', timezone ? `timezone: ${timezone}` : '');
      const endpoint = `/v1/lists/${listId}/activate`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const body = {};
      if (signals !== null) body.signals = signals;
      if (sellerValueProp !== null) body.seller_value_prop = sellerValueProp;
      if (sellerProofPoint !== null) body.seller_proof_point = sellerProofPoint;
      if (templateId !== null) body.template_id = templateId;
      if (templateVersionId !== null) body.template_version_id = templateVersionId;
      if (forceRefresh) body.force_refresh = true;
      if (timezone) body.timezone = timezone;
      if (connectionNoteEnabled !== null) body.connection_note_enabled = connectionNoteEnabled;
      if (connectionNoteTemplate !== null) body.connection_note_template = connectionNoteTemplate;

      // Sequence steps (new architecture)
      if (sequenceSteps !== null) {
        body.sequence_steps = sequenceSteps;
      } else {
        // Legacy fallback
        if (outreachType) body.outreach_type = outreachType;
        if (messageTemplateId) body.message_template_id = messageTemplateId;
        if (messageTemplateVersionId) body.message_template_version_id = messageTemplateVersionId;
      }

      const response = await api.put(endpoint, body, { headers });

      console.log('Activate list response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error activating list:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to activate list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Deactivate a list
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listId - List ID to deactivate
   * @returns {Promise<Object>} Deactivation response
   */
  async deactivateList(authorizationToken, tenant, listId) {
    try {
      console.log(`Deactivating list ${listId}`);
      const endpoint = `/v1/lists/${listId}/deactivate`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.put(endpoint, { confirmed: true }, { headers });

      console.log('Deactivate list response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deactivating list:', error);
      // Extract the error message from the response
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to deactivate list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Delete a list entirely (only inactive lists)
   */
  async deleteList(authorizationToken, tenant, listId) {
    try {
      const response = await api.delete(`/v1/lists/${listId}`, {
        headers: {
          'Authorization': `Bearer ${authorizationToken}`,
          'tenant': tenant
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Rename an imported list (updates its display name only)
   */
  async renameList(authorizationToken, tenant, listId, listName) {
    try {
      const response = await api.patch(`/v1/lists/${listId}`, { list_name: listName }, {
        headers: {
          'Authorization': `Bearer ${authorizationToken}`,
          'tenant': tenant
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get the status of a list activation background job
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} jobId - Job ID to check
   * @returns {Promise<Object>} Job status with progress, message, result
   */
  async getActivationStatus(authorizationToken, tenant, jobId) {
    try {
      const endpoint = `/v1/lists/activation-status/${jobId}`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.get(endpoint, { headers });
      return response.data;
    } catch (error) {
      console.error('Error getting activation status:', error);
      throw error;
    }
  }

  /**
   * Get activation queue status for a list (per-contact progress).
   * @param {string} authorizationToken - JWT token
   * @param {string} tenant - Tenant ID
   * @param {string} listId - List ID
   * @returns {Promise<Object>} Queue status with contacts array, counts, progress_pct, is_complete
   */
  async getActivationQueue(authorizationToken, tenant, listId) {
    try {
      const endpoint = `/v1/lists/${listId}/activation-queue`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;
      const response = await api.get(endpoint, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching activation queue:', error);
      throw error;
    }
  }

  /**
   * Pause an active list
   */
  async pauseList(authorizationToken, tenant, listId) {
    try {
      const endpoint = `/v1/lists/${listId}/pause`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;
      const response = await api.put(endpoint, {}, { headers });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to pause list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Resume a paused list
   */
  async resumeList(authorizationToken, tenant, listId) {
    try {
      const endpoint = `/v1/lists/${listId}/resume`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;
      const response = await api.put(endpoint, {}, { headers });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to resume list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Get deactivation preview (impact counts)
   */
  async getDeactivationPreview(authorizationToken, tenant, listId) {
    try {
      const endpoint = `/v1/lists/${listId}/deactivation-preview`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;
      const response = await api.get(endpoint, { headers });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to get deactivation preview';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Get contact overlap for a list
   */
  async getContactOverlap(authorizationToken, tenant, listId) {
    try {
      const endpoint = `/v1/lists/${listId}/contact-overlap`;
      const headers = {};
      if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
      if (tenant) headers['tenant'] = tenant;
      const response = await api.get(endpoint, { headers });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to check contact overlap';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Get summary of active lists (companies and contacts counts)
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @returns {Promise<Object>} Summary with total_companies and total_contacts
   */
  async getListsSummary(authorizationToken, tenant) {
    try {
      console.log('Fetching lists summary');
      const endpoint = `/v1/lists/summary`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await retryRequest(async () => {
        return await api.get(endpoint, { headers });
      });

      console.log('Lists summary response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching lists summary:', error);
      throw error;
    }
  }

  /**
   * Preview CSV upload - parses file and returns auto-detected mapping
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {File} file - CSV file to preview
   * @returns {Promise<Object>} Preview data with detected mapping and validation warnings
   */
  async previewCSVUpload(authorizationToken, tenant, file) {
    try {
      console.log('Previewing CSV upload:', file.name);
      const endpoint = `/v1/lists/upload-csv/preview`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(endpoint, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('CSV preview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error previewing CSV:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to preview CSV';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Confirm CSV upload - saves the list with the provided mapping
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {File} file - CSV file to upload
   * @param {string} listId - Generated list ID from preview
   * @param {string} listName - User-provided list name
   * @param {Object} fieldMapping - Mapping of our fields to CSV columns
   * @returns {Promise<Object>} Upload result with contacts imported count
   */
  async confirmCSVUpload(authorizationToken, tenant, file, listId, listName, fieldMapping) {
    try {
      console.log('Confirming CSV upload:', listId, listName);
      const endpoint = `/v1/lists/upload-csv/confirm`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('list_id', listId);
      formData.append('list_name', listName);
      formData.append('field_mapping', JSON.stringify(fieldMapping));

      const response = await api.post(endpoint, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('CSV confirm response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error confirming CSV upload:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload CSV';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Sync a list from HubSpot to check for LinkedIn URL updates (async with polling)
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listId - List ID to sync
   * @param {Function} onProgress - Optional callback for progress updates
   * @returns {Promise<Object>} Sync result with contacts_updated count
   */
  async syncList(authorizationToken, tenant, listId, onProgress = null) {
    try {
      console.log(`Starting sync for list ${listId} from HubSpot`);
      const endpoint = `/v1/lists/${listId}/sync`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      // Start the sync (returns job_id immediately)
      const response = await api.post(endpoint, {}, { headers });

      console.log('Sync list queued:', response.data);

      // If the response has a job_id, poll for completion
      if (response.data.job_id) {
        return await this.pollSyncStatus(authorizationToken, tenant, response.data.job_id, onProgress);
      }

      // Fallback for old response format
      return response.data;
    } catch (error) {
      console.error('Error syncing list:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to sync list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Poll for sync job status until completion
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} jobId - Job ID to poll
   * @param {Function} onProgress - Optional callback for progress updates
   * @returns {Promise<Object>} Sync result
   */
  async pollSyncStatus(authorizationToken, tenant, jobId, onProgress = null) {
    const headers = {};
    if (authorizationToken) {
      headers['Authorization'] = 'Bearer ' + authorizationToken;
    }
    if (tenant) {
      headers['tenant'] = tenant;
    }

    const endpoint = `/v1/lists/sync-status/${jobId}`;
    const baseInterval = 3000; // Start at 3 seconds
    const maxInterval = 10000; // Max 10 seconds
    const maxAttempts = 40; // ~3 minutes max with backoff

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await api.get(endpoint, { headers });
        const status = response.data;

        console.log(`Sync status (attempt ${attempt + 1}):`, status);

        // Report progress if callback provided
        if (onProgress) {
          onProgress({
            progress: status.progress || 0,
            message: status.message || 'Processing...'
          });
        }

        // Check if completed
        if (status.status === 'completed') {
          console.log('Sync completed:', status.result);
          return {
            status: 'success',
            ...status.result
          };
        }

        // Check if failed
        if (status.status === 'failed') {
          throw new Error(status.error || 'Sync failed');
        }

        // Exponential backoff: 3s, 4.5s, 6.75s... capped at 10s
        const interval = Math.min(baseInterval * Math.pow(1.5, attempt), maxInterval);
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        // If it's a network error, keep trying
        if (error.response?.status !== 404 && attempt < maxAttempts - 1) {
          console.warn('Poll attempt failed, retrying...', error);
          const interval = Math.min(baseInterval * Math.pow(1.5, attempt), maxInterval);
          await new Promise(resolve => setTimeout(resolve, interval));
        } else {
          throw error;
        }
      }
    }

    throw new Error('Sync timed out');
  }

  /**
   * Update a CSV contact's LinkedIn URL
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listId - CSV list ID
   * @param {string} contactId - Contact ID within the list
   * @param {string} linkedinUrl - LinkedIn URL to set
   * @returns {Promise<Object>} Update result
   */
  async updateCSVContactLinkedIn(authorizationToken, tenant, listId, contactId, linkedinUrl) {
    try {
      console.log(`Updating LinkedIn URL for contact ${contactId} in list ${listId}`);
      const endpoint = `/v1/lists/csv-contact/${listId}/${contactId}`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.patch(endpoint, { linkedin_url: linkedinUrl }, { headers });

      console.log('Update CSV contact response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating CSV contact LinkedIn URL:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update contact';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Update a list's timezone for scheduling
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} selectedListUuid - The UUID of the SelectedList record
   * @param {string} timezone - IANA timezone string (e.g., 'America/New_York')
   * @returns {Promise<Object>} Update result
   */
  async updateListTimezone(authorizationToken, tenant, selectedListUuid, timezone) {
    try {
      console.log(`Updating timezone for list ${selectedListUuid} to ${timezone}`);
      const endpoint = `/v1/lists/${selectedListUuid}/timezone`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.put(endpoint, { timezone }, { headers });

      console.log('Update timezone response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating list timezone:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update timezone';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  async getSequenceConfig(authorizationToken, tenant, listId) {
    const response = await api.get(`/v1/lists/${listId}/sequence-config`, {
      headers: { 'Authorization': `Bearer ${authorizationToken}`, 'tenant': tenant }
    });
    return response.data;
  }

  async updateSequenceVersions(authorizationToken, tenant, listId, steps) {
    const response = await api.put(`/v1/lists/${listId}/sequence-versions`, { steps }, {
      headers: { 'Authorization': `Bearer ${authorizationToken}`, 'tenant': tenant }
    });
    return response.data;
  }

  /**
   * Create a list from selected LinkedIn connections
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listName - Name for the new list
   * @param {Array} contacts - Array of LinkedIn contacts to import
   * @returns {Promise<Object>} Created list info
   */
  async createListFromLinkedIn(authorizationToken, tenant, listName, contacts, source = 'connections') {
    try {
      console.log(`Creating LinkedIn list '${listName}' with ${contacts.length} contacts (source: ${source})`);
      const endpoint = `/v1/lists/create-from-linkedin`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.post(endpoint, {
        list_name: listName,
        contacts: contacts,
        source: source
      }, { headers });

      console.log('Create LinkedIn list response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating LinkedIn list:', error);
      const detail = error.response?.data?.detail;
      const errorMessage = (typeof detail === 'string' ? detail : null) || error.message || 'Failed to create list';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  /**
   * Add LinkedIn contacts to an existing CSV or LinkedIn list
   * @param {string} authorizationToken - JWT token for authentication
   * @param {string} tenant - Tenant ID
   * @param {string} listId - List ID to add contacts to
   * @param {Array} contacts - Array of LinkedIn contacts to add
   * @returns {Promise<Object>} Result with contacts added/skipped counts
   */
  async addLinkedInContactsToList(authorizationToken, tenant, listId, contacts) {
    try {
      console.log(`Adding ${contacts.length} LinkedIn contacts to list ${listId}`);
      const endpoint = `/v1/lists/${listId}/add-linkedin-contacts`;

      const headers = {};

      if (authorizationToken) {
        headers['Authorization'] = 'Bearer ' + authorizationToken;
      }

      if (tenant) {
        headers['tenant'] = tenant;
      }

      const response = await api.post(endpoint, {
        contacts: contacts
      }, { headers });

      console.log('Add LinkedIn contacts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding LinkedIn contacts to list:', error);
      const detail = error.response?.data?.detail;
      const errorMessage = (typeof detail === 'string' ? detail : null) || error.message || 'Failed to add contacts';
      const enhancedError = new Error(errorMessage);
      enhancedError.status = error.response?.status;
      throw enhancedError;
    }
  }

  async getListContacts(authorizationToken, tenant, listId, limit = 50, offset = 0) {
    const headers = {};
    if (authorizationToken) headers.Authorization = `Bearer ${authorizationToken}`;
    if (tenant) headers.tenant = tenant;
    const response = await api.get(`/v1/lists/${encodeURIComponent(listId)}/contacts?limit=${limit}&offset=${offset}`, { headers });
    return response.data;
  }

  async excludeContact(authorizationToken, tenant, listId, contactId) {
    const headers = {};
    if (authorizationToken) headers.Authorization = `Bearer ${authorizationToken}`;
    if (tenant) headers.tenant = tenant;
    const response = await api.post(`/v1/lists/${encodeURIComponent(listId)}/contacts/${encodeURIComponent(contactId)}/exclude`, {}, { headers });
    return response.data;
  }

  async unexcludeContact(authorizationToken, tenant, listId, contactId) {
    const headers = {};
    if (authorizationToken) headers.Authorization = `Bearer ${authorizationToken}`;
    if (tenant) headers.tenant = tenant;
    const response = await api.delete(`/v1/lists/${encodeURIComponent(listId)}/contacts/${encodeURIComponent(contactId)}/exclude`, { headers });
    return response.data;
  }

  async createListFromEngagers(authToken, tenant, listName, engagers) {
    const headers = {};
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
    if (tenant) headers.tenant = tenant;
    const response = await api.post('/v1/lists/create-from-engagers', {
      list_name: listName,
      engagers,
    }, { headers });
    return response.data;
  }

  async addEngagersToList(authToken, tenant, listId, engagers) {
    const headers = {};
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
    if (tenant) headers.tenant = tenant;
    const response = await api.post(`/v1/lists/${encodeURIComponent(listId)}/add-engagers`, {
      engagers,
    }, { headers });
    return response.data;
  }

  async searchHubSpotLists(authorizationToken, tenant, query = '', limit = 50, offset = 0) {
    const headers = {};
    if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
    if (tenant) headers['tenant'] = tenant;
    const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
    const response = await api.get(`/v1/lists/hubspot/search?${params.toString()}`, { headers });
    return response.data;
  }

  async importHubSpotList(authorizationToken, tenant, hubspotListId) {
    const headers = {};
    if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
    if (tenant) headers['tenant'] = tenant;
    const response = await api.post('/v1/lists/hubspot/import', { hubspot_list_id: hubspotListId }, { headers });
    return response.data;
  }

  async searchSalesforceLists(authorizationToken, tenant, query = '', limit = 50, offset = 0) {
    const headers = {};
    if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
    if (tenant) headers['tenant'] = tenant;
    const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
    const response = await api.get(`/v1/lists/salesforce/search?${params.toString()}`, { headers });
    return response.data;
  }

  async importSalesforceList(authorizationToken, tenant, salesforceListId, listName = null) {
    const headers = {};
    if (authorizationToken) headers['Authorization'] = 'Bearer ' + authorizationToken;
    if (tenant) headers['tenant'] = tenant;
    const body = { salesforce_list_id: salesforceListId };
    if (listName && listName.trim()) body.list_name = listName.trim();
    const response = await api.post('/v1/lists/salesforce/import', body, { headers });
    return response.data;
  }
}

export const listsService = new ListsService();
export default listsService;
